<template>
    <div>
        <form v-loading="loading" @submit.prevent="save">
            <div class="card max-w-md mx-auto">
                <h4 class="card__title" v-text="title" />

                <div class="form-group form-group-floating-label">
                    <input
                        id="sales_period_name"
                        v-model="salesPeriod.name"
                        type="text"
                        placeholder=" "
                        required
                        class="form-input"
                    >
                    <label for="sales_period_name" class="floating-label">
                        Säljomgångens namn
                    </label>
                    <input-error :errors="errors" name="salesPeriod.name" />
                </div>

                <div v-if="! inEdit" class="form-group form-select form-group-floating-label">
                    <select
                        id="sales_period_team"
                        v-model="salesPeriod.teamId"
                        class="form-input"
                    >
                        <option value="">Inget säljteam</option>
                        <option disabled>---------</option>
                        <option
                            v-for="team in teams"
                            :value="team.id"
                            :selected="salesPeriod.teamId === team.id"
                        >
                            {{ team.name }}
                        </option>
                    </select>
                    <label for="sales_period_team" class="floating-label">
                        Säljteam
                    </label>
                    <select-arrow />
                </div>
                <div v-else class="mb-xl">
                    <strong class="mb-xs">Säljteam</strong>
                    <div v-if="currentTeam">
                        {{ currentTeam.name }}
                    </div>
                    <div v-else>
                        <em>Inget säljteam valt</em>
                    </div>
                </div>

                <user-select
                    v-model="salesPeriod.sales_manager_user_id"
                    name="sales_manager_user_id"
                    :initial-selected-user="initialSelectedSalesManager"
                    placeholder="Välj säljansvarig"
                    :required="true"
                    :available-users="availableSalesManagers"
                >
                    Säljansvarig
                </user-select>
                <input-error
                    :errors="errors"
                    name="salesPeriod.sales_manager_user_id"
                    class="-mt-md mb-sm block"
                />

                <div v-if="project.prospected_by_team" class="mb-lg -mt-md">
                    <span class="headline-sm font-bold">
                        Området kontrollerat av:
                    </span>
                    {{ project.prospected_by_team.name }}
                </div>

                <div class="form-group form-group-floating-label">
                    <input
                        id="orders_min_days_old"
                        v-model="salesPeriod.orders_min_days_old"
                        name="orders_min_days_old"
                        type="number"
                        min="0"
                        placeholder=" "
                        class="form-input"
                    >
                    <label for="orders_min_days_old" class="floating-label">
                        Ångerfrist (dagar)
                    </label>
                    <input-error :errors="errors" name="salesPeriod.ordersMinDaysOld" />
                </div>

                <sales-period-products
                    :products="products"
                    :selected-products="salesPeriod.products"
                    :update-product="updateProduct"
                    :update-products="updateProducts"
                />
                <input-error :errors="errors" name="salesPeriod.productIds" />

                <div
                    v-if="! existingSalesPeriod"
                    class="mt-lg mb-sm"
                >
                    <h4 class="mb-md">Adresser</h4>
                    <checkbox-input v-model="addUnsignedAddresses">
                        Inkludera alla osignerade adresser
                    </checkbox-input>
                </div>
            </div>
            <div class="button-group max-w-md mx-auto">
                <a
                    class="btn btn-md is-ghost md:min-w-lg"
                    :href="backUrl"
                >
                    Avbryt
                </a>
                <button
                    class="btn btn-md btn-md is-primary md:min-w-lg"
                    type="submit"
                >
                    Spara
                </button>
            </div>
        </form>
    </div>
</template>

<script>
    import SalesPeriodProducts from './SalesPeriodProducts';

    export default {
        components: { SalesPeriodProducts },
        props: {
            project: {
                required: true,
                type: Object,
            },
            products: {
                required: true,
                type: Array,
            },
            teams: {
                required: true,
                type: Array,
            },
            existingSalesPeriod: {
                type: Object,
                default: null,
            },
            title: {
                required: false,
                type: String,
                default: 'Starta säljomgång',
            },
            inEdit: {
                required: false,
                type: Boolean,
                default: false,
            },
            errors: {
                type: Object,
                default: null,
            },
            loading: {
                type: Boolean,
            },
            initialSelectedSalesManager: {
                required: false,
                type: Number,
                default: null,
            },
            availableSalesManagers: {
                required: true,
                type: Object,
            },
        },

        data() {
            return {
                currentTeam: null,
                salesPeriod: {
                    name: '',
                    teamId: '',
                    products: [],
                    sales_manager_user_id: '',
                },
                addUnsignedAddresses: true,
            };
        },

        computed: {
            backUrl() {
                return route('admin.sales-projects.show', this.project);
            },
        },

        created() {
            if (this.existingSalesPeriod) {
                this.currentTeam = this.existingSalesPeriod.team;

                this.salesPeriod = {
                    name: this.existingSalesPeriod.name,
                    products: this.existingSalesPeriod.products,
                    teamId: this.existingSalesPeriod.team_id,
                    orders_min_days_old: this.existingSalesPeriod.orders_min_days_old,
                };

                // this.teams originally only consists of active teams,
                // but the (ev) existing team might be inactive and
                // might therefore not be a part of this.teams.
                // Add it to this.teams so it can be selected.
                const existingTeam = this.existingSalesPeriod.team;
                if (existingTeam && ! existingTeam.active) {
                    this.teams.push(existingTeam);
                }
            }
        },

        methods: {
            updateProduct(product) {
                const index = this.salesPeriod.products.findIndex(selectedProduct => selectedProduct.id === product.id);

                if (index < 0) {
                    this.salesPeriod.products.push(product);
                } else {
                    this.salesPeriod.products.splice(index, 1);
                }
            },

            updateProducts(products) {
                this.salesPeriod.products = products;
            },

            save() {
                // We need to assemble the product's IDs for the controller
                // so the request validates
                this.salesPeriod.productIds = this.salesPeriod.products.map(salesPeriod => salesPeriod.id);

                this.$emit('save', this.salesPeriod, this.addUnsignedAddresses);
            },
        },
    };
</script>
